<script>
	import * as Pancake from '@sveltejs/pancake';
	import points from './points.js';

	const bmigroup = ['underweight', 'normal', 'overweight', 'obesity'];

	// Bands, in the order they appear on the axis. The Asian/Singapore cut-offs
	// differ from WHO's - that difference is the whole point of this tool, so
	// both rows stay on the chart and the reading says which one it used.
	// Two colours per band, deliberately. `color` fills a chart band, where it
	// sits on white in a large solid block. `ink` is for TEXT, which needs
	// 4.5:1 against white - the band fills do not reach it (#e0a94a is about
	// 2:1), so setting a 16px word or a 64px number in them would fail.
	const BANDS = [
		{ key: 'underweight', label: 'Underweight', color: '#7fb2bb', ink: '#3d6b74' },
		{ key: 'normal', label: 'Healthy', color: '#018e9f', ink: '#014d58' },
		{ key: 'overweight', label: 'Overweight', color: '#e0a94a', ink: '#8a5a06' },
		{ key: 'obesity', label: 'Obese', color: '#cf6b4b', ink: '#9c3f24' }
	];

	// points.js: cat 1 = WHO 2019, cat 2 = Singapore. Read it off the data, not
	// off the order in the file: cat 1's healthy band runs to 18.5+6.4 = 24.9
	// (the WHO threshold) and cat 2's to 18.5+4.4 = 22.9 (Singapore's lower
	// Asian cut-off). Getting this backwards silently reports a BMI of 23.5 as
	// "Healthy" while telling the reader it used Singapore cut-offs, which are
	// the entire reason this tool exists.
	// Each value is the WIDTH of a band, so the cut-offs are running totals.
	const SG = points.find((p) => p.cat === 2);
	function cutoffs(row) {
		let acc = 0;
		return bmigroup.map((k) => (acc += row[k]));
	}
	const SG_CUTS = cutoffs(SG);

	function bandFor(bmi) {
		for (let i = 0; i < SG_CUTS.length; i++) {
			if (bmi < SG_CUTS[i]) return BANDS[i];
		}
		return BANDS[BANDS.length - 1];
	}

	function format_x(str) {
		const ylabel = ['WHO 2019', 'Singapore'];
		return ylabel[parseInt(str) - 1];
	}

	function clamp(val, min, max) {
		return val > max ? max : val < min ? min : val;
	}

	const stacks = Pancake.stacks(points, bmigroup, 'cat');
	const max = stacks.reduce(
		(m, stack) => Math.max(m, ...stack.values.map((v) => v.end)),
		0
	);

	let wt = 60;
	let ht = 170;

	// Guard the inputs: a cleared number field yields undefined, and a height
	// of 0 divides to Infinity. Both used to render "BMI = NaN".
	$: valid = Number.isFinite(+wt) && Number.isFinite(+ht) && +wt > 0 && +ht > 0;
	$: bmi = valid ? +wt / Math.pow(+ht / 100, 2) : NaN;
	$: shown = valid ? Math.round(bmi * 10) / 10 : null;
	$: band = valid ? bandFor(bmi) : null;
	// A vertical rule through BOTH rows, not a dot on one of them. The dot sat
	// on the WHO row while the "You" label floated above the Singapore row, so
	// they read as marking different things - and the point of the chart is
	// that one BMI lands in different bands under the two standards.
	$: markerLine = [
		{ x: clamp(bmi, 0, max), y: 0.4 },
		{ x: clamp(bmi, 0, max), y: 2.6 }
	];

</script>

<div class="bmi">
	<header class="masthead">
		<p class="eyebrow">Calculator</p>
		<h1>Body Mass Index</h1>
		<p class="lede">
			Singapore uses lower cut-offs than the WHO standard. Both are shown.
		</p>
	</header>

	<div class="inputs">
		<div class="field">
			<label for="bmi-wt">Weight <span class="unit">kg</span></label>
			<div class="control">
				<input id="bmi-wt" type="number" bind:value={wt} min="1" max="300" />
				<input
					class="slider"
					type="range"
					bind:value={wt}
					min="10"
					max="200"
					aria-label="Weight in kilograms"
				/>
			</div>
		</div>

		<div class="field">
			<label for="bmi-ht">Height <span class="unit">cm</span></label>
			<div class="control">
				<input id="bmi-ht" type="number" bind:value={ht} min="1" max="250" />
				<input
					class="slider"
					type="range"
					bind:value={ht}
					min="100"
					max="200"
					aria-label="Height in centimetres"
				/>
			</div>
		</div>
	</div>

	<section class="result" aria-live="polite">
		<div class="result-grid">
			<div class="scoreblock">
				<p class="result-label">Your BMI</p>
				{#if valid}
					<p class="score">
						<span class="score-n" style="color: {band.ink}">{shown}</span>
					</p>
					<p class="reading">
						In the <strong style="color: {band.ink}">{band.label}</strong> range
						by Singapore cut-offs.
					</p>
				{:else}
					<p class="score"><span class="score-n muted">&mdash;</span></p>
					<p class="reading">Enter a weight and height.</p>
				{/if}
				<p class="source">
					Cut-offs:
					<cite>HPB/MOH Singapore</cite> and <cite>WHO, 2019</cite>
				</p>
			</div>

			<figure class="chartblock">
				<div class="chart">
					<Pancake.Chart x1={0} x2={max} y1={0.1} y2={2.9}>
						<Pancake.Grid horizontal count={2} let:value>
							<div class="grid-line horizontal"><span>{format_x(value)}</span></div>
						</Pancake.Grid>
						<Pancake.Grid vertical count={10} let:value>
							<span class="x-label">{value}</span>
						</Pancake.Grid>

						{#each stacks as stack, i}
							{#each stack.values as d}
								<Pancake.Box x1={d.start} x2={d.end} y1={d.i - 0.42} y2={d.i + 0.42}>
									<div class="box" style="background-color: {BANDS[i].color}"></div>
								</Pancake.Box>
							{/each}
						{/each}

						{#if valid}
							<Pancake.Svg>
								<Pancake.SvgLine data={markerLine} x={(d) => d.x} y={(d) => d.y} let:d>
									<path class="marker-casing" {d} />
									<path class="marker-rule" {d} />
								</Pancake.SvgLine>
							</Pancake.Svg>
							<Pancake.Point x={clamp(bmi, 0, max)} y={2.6}>
								<span class="you-label">You</span>
							</Pancake.Point>
						{/if}
					</Pancake.Chart>
				</div>
				<figcaption>BMI bands under each standard</figcaption>
			</figure>
		</div>

		<ul class="key">
			{#each BANDS as b}
				<li class:active={valid && band.key === b.key}>
					<span class="swatch" style="background: {b.color}"></span>{b.label}
				</li>
			{/each}
		</ul>
	</section>
</div>

<style>
	.bmi {
		max-width: 880px;
		margin: 0 auto;
		padding: var(--space-md) var(--space-md) var(--space-xl);
		color: var(--ink);
		font-family: 'Instrument Sans', -apple-system, BlinkMacSystemFont,
			'Segoe UI', Roboto, sans-serif;
		font-size: 16px;
		line-height: 1.5;
	}

	.masthead {
		margin-bottom: var(--space-lg);
	}
	.eyebrow {
		margin: 0 0 var(--space-xs);
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: var(--teal-text);
	}
	h1 {
		margin: 0 0 var(--space-sm);
		font-size: clamp(24px, 3.2vw, 32px);
		font-weight: 650;
		line-height: 1.15;
		letter-spacing: -0.015em;
		color: var(--ink);
	}
	.lede {
		margin: 0;
		max-width: 52ch;
		color: var(--text-2);
	}

	/* ---- inputs --------------------------------------------------------- */
	.inputs {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
		gap: var(--space-lg);
		margin-bottom: var(--space-lg);
	}
	.field label {
		display: block;
		margin-bottom: var(--space-xs);
		font-size: 13px;
		font-weight: 600;
		letter-spacing: 0.02em;
	}
	.unit {
		font-weight: 500;
		color: var(--text-2);
	}
	.control {
		display: flex;
		align-items: center;
		gap: var(--space-sm);
	}
	.control input[type='number'] {
		width: 84px;
		min-height: 44px;
		margin: 0;
		padding: 8px 10px;
		border: 1.5px solid var(--line);
		border-radius: 8px;
		background: var(--paper);
		font: inherit;
		font-size: 16px; /* 16px stops iOS zooming the page on focus */
		font-variant-numeric: tabular-nums;
		color: var(--ink);
	}
	.control input[type='number']:focus-visible {
		outline: 2px solid var(--teal);
		outline-offset: 1px;
		border-color: var(--teal);
	}

	.slider {
		flex: 1;
		/* 44px of grabbable height. The track and thumb are centred inside it,
		   so this is invisible — the number input beside it is already 44px,
		   and the row does not grow. */
		height: 44px;
		margin: 0;
		padding: 0;
		border: 0;
		background: transparent;
		-webkit-appearance: none;
		appearance: none;
		cursor: pointer;
	}
	.slider::-webkit-slider-runnable-track {
		height: 4px;
		border-radius: 2px;
		background: var(--line);
	}
	.slider::-moz-range-track {
		height: 4px;
		border-radius: 2px;
		background: var(--line);
	}
	.slider::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		width: 20px;
		height: 20px;
		margin-top: -8px;
		border-radius: 50%;
		border: 2px solid #fff;
		background: var(--teal);
		box-shadow: 0 1px 3px rgba(1, 77, 88, 0.35);
	}
	.slider::-moz-range-thumb {
		width: 20px;
		height: 20px;
		border-radius: 50%;
		border: 2px solid #fff;
		background: var(--teal);
	}
	.slider:focus-visible {
		outline: 2px solid var(--teal);
		outline-offset: 4px;
	}

	/* ---- result --------------------------------------------------------- */
	.result {
		border-top: 1px solid var(--line);
		padding-top: var(--space-lg);
	}
	.result-grid {
		display: grid;
		grid-template-columns: minmax(200px, 280px) 1fr;
		gap: var(--space-xl);
		align-items: start;
	}
	@media (max-width: 640px) {
		.result-grid {
			grid-template-columns: 1fr;
			gap: var(--space-lg);
		}
	}
	.result-label {
		margin: 0;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: var(--teal-text);
	}
	.score {
		margin: 2px 0 var(--space-sm);
	}
	.score-n {
		font-size: 64px;
		font-weight: 700;
		line-height: 1;
		letter-spacing: -0.03em;
		font-variant-numeric: tabular-nums;
		transition: color 0.25s var(--ease-out);
	}
	.score-n.muted {
		color: var(--line);
	}
	.reading {
		margin: 0;
		max-width: 34ch;
		font-size: 16px;
		line-height: 1.55;
	}
	.reading strong {
		font-weight: 700;
	}
	.source {
		margin: var(--space-md) 0 0;
		padding-top: var(--space-sm);
		border-top: 1px solid var(--line);
		font-size: 12px;
		line-height: 1.45;
		color: var(--text-2);
	}
	.source cite {
		font-style: normal;
		font-weight: 600;
		color: var(--ink);
	}

	/* ---- chart ---------------------------------------------------------- */
	.chartblock {
		margin: 0;
	}
	.chart {
		position: relative;
		height: 150px;
		padding: 24px 8px 26px 74px;
	}
	.grid-line.horizontal {
		position: relative;
		display: block;
		width: 100%;
	}
	.grid-line.horizontal span {
		position: absolute;
		left: -72px;
		bottom: -8px;
		font-size: 11px;
		font-weight: 600;
		color: var(--text-2);
	}
	.x-label {
		position: absolute;
		width: 3em;
		left: -1.5em;
		bottom: -22px;
		font-size: 11px;
		text-align: center;
		color: var(--text-2);
		font-variant-numeric: tabular-nums;
	}
	.box {
		position: absolute;
		left: 0;
		top: 2px;
		width: 100%;
		height: calc(100% - 4px);
		border-radius: 3px;
	}
	/* A white casing under the rule so it stays legible crossing the darker
	   bands, the same trick a map uses for a route over varied terrain. */
	.marker-casing {
		stroke: #fff;
		stroke-width: 6px;
		stroke-linecap: round;
		opacity: 0.9;
		fill: none;
	}
	.marker-rule {
		stroke: var(--ink);
		stroke-width: 2.5px;
		stroke-linecap: round;
		fill: none;
	}
	.you-label {
		position: absolute;
		transform: translate(-50%, -15px);
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--ink);
	}
	figcaption {
		margin-top: var(--space-sm);
		padding-left: 74px;
		font-size: 12px;
		color: var(--text-2);
	}

	/* ---- key ------------------------------------------------------------ */
	.key {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-sm) var(--space-md);
		margin: var(--space-lg) 0 0;
		padding: 0;
		list-style: none;
		font-size: 12px;
		color: var(--text-2);
	}
	.key li {
		display: flex;
		align-items: center;
		gap: 6px;
	}
	.key li.active {
		color: var(--ink);
		font-weight: 600;
	}
	.swatch {
		width: 10px;
		height: 10px;
		border-radius: 3px;
	}

	/* ---- phones --------------------------------------------------------- */
	@media (max-width: 480px) {
		.bmi {
			padding: var(--space-sm) var(--space-md) var(--space-md);
			font-size: 15px;
		}
		.masthead {
			margin-bottom: var(--space-md);
		}
		h1 {
			font-size: 22px;
		}
		.lede {
			font-size: 14px;
		}
		.inputs {
			gap: var(--space-md);
			margin-bottom: var(--space-md);
		}
		.result {
			padding-top: var(--space-md);
		}
		.score-n {
			font-size: 52px;
		}
		.chart {
			height: 130px;
			padding-left: 66px;
		}
		.grid-line.horizontal span {
			left: -64px;
		}
		figcaption {
			padding-left: 66px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.score-n {
			transition: none;
		}
	}
</style>
